const TAU = Math.PI * 2

/** One band read as a single halo: three sub-layers that overlap in radius. */
const LAYERS = [
  { count: 96, dur: 58, dir: 1, inner: 40, outer: 47, min: 0.16, max: 0.5 },
  { count: 78, dur: 42, dir: -1, inner: 35, outer: 43.5, min: 0.14, max: 0.42 },
  { count: 60, dur: 29, dir: 1, inner: 30.5, outer: 39, min: 0.12, max: 0.34 },
] as const

/** The five strengths of the shades, as alpha multipliers of one hue. */
const STRENGTHS = [0.7, 1, 0.9, 0.62, 0.38] as const

/** Seconds per crest sweep around the band. */
const CREST = 3.2

/** Sphere radius in the 0..100 box. */
export const SPHERE_RADIUS = 40

/** Dot count of the small mark. */
export const MARK_DOTS = 44

/** Seconds the small mark spends in each part of its cycle: ring held, pour into the sphere, sphere held, pour back. */
export interface PourTimes {
  ringHold: number
  pour: number
  sphereHold: number
  pourBack: number
}

export const MARK_POUR: PourTimes = { ringHold: 1.6, pour: 1.2, sphereHold: 2.4, pourBack: 1.2 }

/** Field time in the middle of the sphere hold, for a mark drawn once and left still. */
export const SPHERE_HOLD_TIME = MARK_POUR.ringHold + MARK_POUR.pour + MARK_POUR.sphereHold / 2

const MARK_RING_RADIUS = 36
const MARK_RING_SPREAD = 7.5
const MARK_RING_TURN_SECONDS = 12.5

const GOLDEN_ANGLE = 2.39996323
const GLOBE_SPIN = 0.45
const GLOBE_TILT = 0.35

/** Deterministic PRNG (mulberry32), so a given seed reproduces the same scatter every time. */
function seeded(seed: number): () => number {
  let state = seed >>> 0
  return () => {
    state = (state + 0x6d2b79f5) >>> 0
    let value = Math.imul(state ^ (state >>> 15), 1 | state)
    value = (value + Math.imul(value ^ (value >>> 7), 61 | value)) ^ value
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t)
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v
}

export interface Mote {
  /** Longitude of the mote's sphere home, in radians. */
  sphereTheta: number
  /** 0..1 height of the mote's sphere home, pole to pole. */
  sphereV: number
  turn: number
  rad: number
  v: number
  li: number
  dur: number
  dir: 1 | -1
  r: number
  strength: number
  off: number
  lag: number
  ph: number
  ph2: number
  ph3: number
  spd: number
}

interface Tempo {
  tempo?: 'calm' | 'quick' | 'still'
}

/** How the sphere turns: `plane` spins the whole field in the screen plane, `globe` turns the ball on a tilted vertical axis. */
type FieldTurn = 'plane' | 'globe'

export interface LayoutOptions extends Tempo {
  turn?: FieldTurn
  /** Replaces the field's ring/sphere schedule with a fixed cycle; `still` then holds the sphere. */
  pour?: PourTimes
}

export interface FieldPoint {
  x: number
  y: number
  z: number
  per: number
  /** 0 on the ring, 1 on the sphere: how far this mote has poured. */
  mix: number
}

export interface Crest {
  op: number
  sc: number
}

/** Builds the mote population for a seed at the given density multiplier (LAYERS counts x density). */
export function buildMotes(seed: number, density: number): Mote[] {
  const rand = seeded(seed)
  const motes: Mote[] = []
  LAYERS.forEach((layer, li) => {
    const count = layer.count * density
    for (let i = 0; i < count; i += 1) {
      const turn = (i + (rand() - 0.5) * 0.85) / count
      const rad = layer.inner + rand() * (layer.outer - layer.inner)
      const v = (rad - 30.5) / (47 - 30.5)
      const off = ((turn % 1) + 1) % 1
      motes.push({
        sphereTheta: off * TAU,
        sphereV: v,
        turn,
        rad,
        v,
        li,
        dur: layer.dur,
        dir: layer.dir,
        r: (layer.min + rand() * (layer.max - layer.min)) * 0.42,
        strength: STRENGTHS[Math.floor(rand() * STRENGTHS.length)] as number,
        off,
        lag: rand(),
        ph: rand() * TAU,
        ph2: rand() * TAU,
        ph3: rand() * TAU,
        spd: 0.7 + rand() * 0.9,
      })
    }
  })
  return motes
}

/** Builds the small mark's motes: `count` dots on a turning ring, each with a home on the sphere. */
export function buildMarkMotes(seed: number, count: number = MARK_DOTS): Mote[] {
  const rand = seeded(seed)
  const motes: Mote[] = []
  for (let index = 0; index < count; index += 1) {
    const rad = MARK_RING_RADIUS + (rand() - 0.5) * MARK_RING_SPREAD
    motes.push({
      sphereTheta: index * GOLDEN_ANGLE,
      sphereV: (index + 0.5) / count,
      turn: index / count,
      rad,
      v: 0.5,
      li: 0,
      dur: MARK_RING_TURN_SECONDS,
      dir: 1,
      r: 1,
      strength: STRENGTHS[Math.floor(rand() * STRENGTHS.length)] as number,
      off: index / count,
      lag: rand(),
      ph: rand() * TAU,
      ph2: rand() * TAU,
      ph3: rand() * TAU,
      spd: 0.7 + rand() * 0.9,
    })
  }
  return motes
}

function homeRing(mote: Mote, a: number): [number, number, number] {
  return [50 + Math.cos(a) * mote.rad, 50 + Math.sin(a) * mote.rad, (mote.v - 0.5) * 4]
}

function homeSphere(mote: Mote): [number, number, number] {
  const th = mote.sphereTheta
  const ph = Math.acos(2 * mote.sphereV - 1)
  const r = SPHERE_RADIUS
  return [
    50 + r * Math.sin(ph) * Math.cos(th),
    50 + r * Math.sin(ph) * Math.sin(th),
    r * Math.cos(ph),
  ]
}

function pourMix(t: number, times: PourTimes): number {
  const { ringHold, pour, sphereHold, pourBack } = times
  const c = t % (ringHold + pour + sphereHold + pourBack)
  if (c < ringHold) return 0
  if (c < ringHold + pour) return (c - ringHold) / pour
  if (c < ringHold + pour + sphereHold) return 1
  return 1 - (c - ringHold - pour - sphereHold) / pourBack
}

function scheduleMix(
  t: number,
  tempo: 'calm' | 'quick' | 'still',
  pour: PourTimes | undefined,
): number {
  if (pour) return tempo === 'still' ? 1 : pourMix(t, pour)
  if (tempo === 'still') return 0
  const hold = tempo === 'quick' ? 2 : 4
  const change = tempo === 'quick' ? 1.6 : 3
  const per = 2 * (hold + change)
  const c = t % per
  if (c < hold) return 0
  if (c < hold + change) return (c - hold) / change
  if (c < 2 * hold + change) return 1
  return 1 - (c - 2 * hold - change) / change
}

function homeGlobe(mote: Mote, T: number): [number, number, number] {
  const ph = Math.acos(1 - 2 * mote.sphereV)
  const r = SPHERE_RADIUS
  const x = r * Math.sin(ph) * Math.cos(mote.sphereTheta)
  const y = r * Math.cos(ph)
  const z = r * Math.sin(ph) * Math.sin(mote.sphereTheta)
  const spin = T * GLOBE_SPIN
  const X = x * Math.cos(spin) + z * Math.sin(spin)
  const Z = -x * Math.sin(spin) + z * Math.cos(spin)
  const Y = y * Math.cos(GLOBE_TILT) - Z * Math.sin(GLOBE_TILT)
  return [50 + X, 50 + Y, y * Math.sin(GLOBE_TILT) + Z * Math.cos(GLOBE_TILT)]
}

function layoutOne(
  mote: Mote,
  T: number,
  tempo: 'calm' | 'quick' | 'still',
  turn: FieldTurn,
  pour: PourTimes | undefined,
): FieldPoint {
  const still = tempo === 'still' && !pour
  const breathe = 1.01 + 0.025 * Math.sin((T * TAU) / 7.6)
  const contract = still ? 0.92 : 1
  const mix = scheduleMix(T, tempo, pour)
  const yaw = turn === 'plane' ? T * 0.1 : 0
  const cyw = Math.cos(yaw)
  const syw = Math.sin(yaw)

  const a = (mote.turn + mote.dir * (T / mote.dur)) * TAU
  const A = homeRing(mote, a)
  const B = turn === 'plane' ? homeSphere(mote) : homeGlobe(mote, T)
  const k = smooth(clamp01((mix - mote.lag * 0.5) / 0.5))
  let x = A[0] + (B[0] - A[0]) * k
  let y = A[1] + (B[1] - A[1]) * k
  let z = A[2] + (B[2] - A[2]) * k

  const d = 0.8
  x += d * Math.sin(T * 0.55 * mote.spd + mote.ph)
  y += d * Math.sin(T * 0.43 * mote.spd + mote.ph2)
  z += d * 1.3 * Math.sin(T * 0.37 * mote.spd + mote.ph3)

  x = 50 + (x - 50) * breathe * contract
  y = 50 + (y - 50) * breathe * contract
  z *= contract

  const X = x - 50
  const Y = y - 50
  const X2 = X * cyw - Y * syw
  const Y2 = X * syw + Y * cyw

  const f = 160
  const per = f / (f - z)
  return { x: 50 + X2 * per, y: 50 + Y2 * per, z, per, mix: k }
}

export function tempoSpeed(tempo: 'calm' | 'quick' | 'still'): number {
  return tempo === 'quick' ? 1.8 : tempo === 'still' ? 0.5 : 1
}

/** Projects every mote at tempo-scaled field time `t` into the 0..100 box, with drift, the ring/sphere pour and the yaw turn. */
export function layoutField(
  motes: Mote[],
  t: number,
  { tempo = 'calm', turn = 'plane', pour }: LayoutOptions = {},
): FieldPoint[] {
  return motes.map((mote) => layoutOne(mote, t, tempo, turn, pour))
}

export function crestAt(t: number, mote: Mote): Crest {
  const phase = ((t + mote.off * CREST + mote.li * 0.12) % CREST) / CREST
  if (phase < 0.07) {
    const k = phase / 0.07
    return { op: 0.42 + 0.58 * k, sc: 0.95 + 0.45 * k }
  }
  if (phase < 0.3) {
    const k = (phase - 0.07) / 0.23
    return { op: 1 - 0.48 * k, sc: 1.4 - 0.4 * k }
  }
  const k = (phase - 0.3) / 0.7
  return { op: 0.52 - 0.1 * k, sc: 1 - 0.05 * k }
}
